import { createContext, useCallback, useMemo } from 'react';
import defaultConfig from 'config';
import useLocalStorage from 'hooks/useLocalStorage';
import {
    ConfigProviderProps,
    CustomizationProps,
    FirstStart,
    FontFamily,
    I18n,
    MenuOrientation,
    PresetColor,
    TabType,
    ThemeDirection,
    ThemeMode,
} from 'types/config';

const initialState: CustomizationProps = {
    ...defaultConfig,
    onChangeContainer: () => {},
    onChangeLocalization: (lang: I18n) => {},
    onChangeMode: (mode: ThemeMode) => {},
    onChangePresetColor: (theme: PresetColor) => {},
    onChangeDirection: (direction: ThemeDirection) => {},
    onChangeMiniDrawer: (miniDrawer: boolean) => {},
    onChangeMenuOrientation: (menuOrientation: MenuOrientation) => {},
    onChangeFontFamily: (fontFamily: FontFamily) => {},
    onChangeFirstStart: (firstStart: FirstStart) => {},
    onChangeShowGuide: (showGuide: boolean) => {},
    onChangeTableOptions: (tableOptions: object) => {},
    onChangeWorkflowExecutionTab: (tab: TabType) => {},
};

const ConfigContext = createContext<CustomizationProps>(initialState);

function ConfigProvider({ children }: ConfigProviderProps) {
    const [configState, setConfig] = useLocalStorage<CustomizationProps>('lexis-config', initialState);

    const onChangeContainer = useCallback(() => {
        setConfig(prevConfig => ({
            ...prevConfig,
            container: !prevConfig.container,
        }));
    }, [setConfig]);

    const onChangeLocalization = useCallback(
        (lang: I18n) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                i18n: lang,
            }));
        },
        [setConfig]
    );

    const onChangeMode = useCallback(
        (mode: ThemeMode) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                mode,
            }));
        },
        [setConfig]
    );

    const onChangePresetColor = useCallback(
        (theme: PresetColor) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                presetColor: theme,
            }));
        },
        [setConfig]
    );

    const onChangeDirection = useCallback(
        (direction: ThemeDirection) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                themeDirection: direction,
            }));
        },
        [setConfig]
    );

    const onChangeMiniDrawer = useCallback(
        (miniDrawer: boolean) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                miniDrawer,
            }));
        },
        [setConfig]
    );

    const onChangeMenuOrientation = useCallback(
        (layout: MenuOrientation) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                menuOrientation: layout,
            }));
        },
        [setConfig]
    );

    const onChangeFontFamily = useCallback(
        (fontFamily: FontFamily) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                fontFamily,
            }));
        },
        [setConfig]
    );

    const onChangeFirstStart = useCallback(
        (firstStart: FirstStart) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                firstStart,
            }));
        },
        [setConfig]
    );

    const onChangeShowGuide = useCallback(
        (showGuide: boolean) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                showGuide,
            }));
        },
        [setConfig]
    );

    const onChangeTableOptions = useCallback(
        (tableOptions: object) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                tableOptions: tableOptions as CustomizationProps['tableOptions'],
            }));
        },
        [setConfig]
    );

    const onChangeWorkflowExecutionTab = useCallback(
        (workflowExecutionTab: TabType) => {
            setConfig(prevConfig => ({
                ...prevConfig,
                workflowExecutionTab,
            }));
        },
        [setConfig]
    );

    const contextValue = useMemo<CustomizationProps>(
        () => ({
            ...configState,
            onChangeContainer,
            onChangeLocalization,
            onChangeMode,
            onChangePresetColor,
            onChangeDirection,
            onChangeMiniDrawer,
            onChangeMenuOrientation,
            onChangeFontFamily,
            onChangeFirstStart,
            onChangeShowGuide,
            onChangeTableOptions,
            onChangeWorkflowExecutionTab,
        }),
        [
            configState,
            onChangeContainer,
            onChangeLocalization,
            onChangeMode,
            onChangePresetColor,
            onChangeDirection,
            onChangeMiniDrawer,
            onChangeMenuOrientation,
            onChangeFontFamily,
            onChangeFirstStart,
            onChangeShowGuide,
            onChangeTableOptions,
            onChangeWorkflowExecutionTab,
        ]
    );

    return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
}

export { ConfigProvider, ConfigContext };
